import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Check, ChevronDown, RefreshCw, RotateCcw, Search, X } from 'lucide-react';
import PillTabs from '../../../../components/common/PillTabs';
import { displayMcpToolName } from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import EmptyState from '../components/EmptyState';
import { ghostBtn, tableHeadClass, tdClass, thClass, trClass } from '../components/GatewayUi';
import {
  aiGovernanceDashboardApi,
  type AiGovernanceStatistics,
  type StatisticsMetric,
  type StatisticsUserRow,
} from '../aiGovernanceDashboardApi';
import { departmentMatches } from '../quotaDirectory';
import type { QuotaDirectory, QuotaDirectoryDepartment, QuotaKind, QuotaUser, QuotaUserStatus } from '../governedMcpTypes';
import {
  aggregateModelSpend,
  compactTokens,
  clientLabel,
  currentQuotaMonth,
  formatQuotaMonth,
  formatTokens,
  quotaStatus,
  remainingTokens,
  resourceTypeFromQuotaKind,
  utilizationBreakdown,
  utilizationPercent,
} from '../quotaUsage';

type FilterDim = 'clientId' | 'resourceId' | 'departmentId' | 'roleId' | 'userId';
type DailyMode = 'total' | 'stack';
type NamedOption = { id: string; name: string };
type DailyBreakdown = { name: string; tokens: number };
type DailyPoint = {
  date: string;
  label: string;
  tokens: number;
  requestCount: number;
  average: number;
  breakdown: DailyBreakdown[];
  [key: string]: string | number | DailyBreakdown[];
};

type StatisticsMapped = Omit<QuotaUser, 'dailyUsage'> & {
  resourceId: string;
  resourceType: string;
  promptTokens: number;
  completionTokens: number;
  requestCount: number;
  dailyUsage: { date: string; tokens: number; requestCount: number }[];
};

const KIND_TABS: { id: QuotaKind; label: string }[] = [
  { id: 'tool', label: 'Tools' },
  { id: 'llm', label: 'AI Gateway' },
  { id: 'client', label: 'Clients' },
];

const STACK_LIMIT = 5;
const RANK_LIMIT = 8;
const LIVE_REFRESH_MS = 15_000;
const STACK_PREFIX = 's::';

function resourceDisplayName(kind: QuotaKind, id: string, name?: string): string {
  const raw = (name || id || '').trim();
  return kind === 'tool' ? displayMcpToolName(raw) : raw;
}

function asStackKey(name: string): string {
  return `${STACK_PREFIX}${name}`;
}

function fromStackKey(key: string): string {
  return key.startsWith(STACK_PREFIX) ? key.slice(STACK_PREFIX.length) : key;
}
const MODEL_COLORS_LIGHT = ['#4f46e5', '#0ea5e9', '#7c3aed', '#059669', '#d97706'];
const MODEL_COLORS_DARK = ['#818cf8', '#38bdf8', '#a78bfa', '#34d399', '#fbbf24'];
const STACK_COLORS_LIGHT = ['#4f46e5', '#0ea5e9', '#7c3aed', '#059669', '#d97706', '#94a3b8'];
const STACK_COLORS_DARK = ['#818cf8', '#38bdf8', '#a78bfa', '#34d399', '#fbbf24', '#64748b'];
const STATUS_COLORS = {
  ok: { light: '#059669', dark: '#34d399' },
  warning: { light: '#d97706', dark: '#fbbf24' },
  exceeded: { light: '#e11d48', dark: '#fb7185' },
};
const PALETTE = {
  daily: { light: '#4f46e5', dark: '#818cf8' },
  people: { light: '#4f46e5', dark: '#818cf8' },
  remain: { light: '#c7d2fe', dark: '#312e81' },
  department: { light: '#0ea5e9', dark: '#38bdf8' },
  role: { light: '#7c3aed', dark: '#a78bfa' },
  client: { light: '#059669', dark: '#34d399' },
  resource: { light: '#d97706', dark: '#fbbf24' },
  average: { light: '#f59e0b', dark: '#fbbf24' },
};

const EMPTY_FILTERS = {
  clientId: 'all',
  resourceId: 'all',
  departmentId: 'all',
  roleId: 'all',
  userId: 'all',
};

function cardClass(isLight: boolean): string {
  return `rounded-2xl p-4 ${
    isLight
      ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04]'
      : 'bg-neutral-900 ring-1 ring-white/10'
  }`;
}

function compactControlClass(isLight: boolean): string {
  return isLight
    ? 'h-8 rounded-lg px-2.5 text-xs bg-white border border-neutral-200 text-neutral-700 transition-shadow focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-400'
    : 'h-8 rounded-lg px-2.5 text-xs bg-neutral-900 border border-neutral-700 text-neutral-200 transition-shadow focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400';
}

function dayLabel(isoDate: string): string {
  const parsed = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function unitLabel(metric: StatisticsMetric): string {
  return metric === 'calls' ? 'Calls' : 'Tokens';
}

function formatMetric(value: number, metric: StatisticsMetric): string {
  return metric === 'calls' ? formatTokens(value) : formatTokens(value);
}

function compactMetric(value: number, metric: StatisticsMetric): string {
  return metric === 'calls' && value < 1000 ? formatTokens(value) : compactTokens(value);
}

function formatUpdatedTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' });
}

function statusLabel(status: QuotaUserStatus): string {
  if (status === 'exceeded') return 'Over limit';
  if (status === 'warning') return 'High';
  return 'On track';
}

function pillTone(status: QuotaUserStatus, isLight: boolean): string {
  if (status === 'exceeded') return isLight ? 'bg-rose-50 text-rose-700' : 'bg-rose-500/15 text-rose-300';
  if (status === 'warning') return isLight ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/15 text-amber-300';
  return isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300';
}

function axisName(value: string, max = 18): string {
  const text = String(value || '');
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function daysInMonth(month: string): string[] {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return [];
  const year = Number(match[1]);
  const monthIndex = Number(match[2]);
  const count = new Date(year, monthIndex, 0).getDate();
  if (!Number.isFinite(count) || count < 1) return [];
  return Array.from({ length: count }, (_, index) => `${month}-${String(index + 1).padStart(2, '0')}`);
}

function userMatchesDepartment(
  user: StatisticsMapped,
  departmentId: string,
  departments: QuotaDirectoryDepartment[]
): boolean {
  const department = departments.find((item) => item.id === departmentId || departmentMatches(item, departmentId));
  if (department) return department.matchIds.includes(user.departmentId || '') || user.departmentId === department.id;
  return user.departmentId === departmentId;
}

function userMatches(
  user: StatisticsMapped,
  filters: typeof EMPTY_FILTERS,
  departments: QuotaDirectoryDepartment[],
  except?: FilterDim
): boolean {
  if (except !== 'clientId' && filters.clientId !== 'all' && user.clientId !== filters.clientId) return false;
  if (except !== 'resourceId' && filters.resourceId !== 'all' && user.resourceId !== filters.resourceId) return false;
  if (except !== 'userId' && filters.userId !== 'all' && user.userId !== filters.userId) return false;
  if (except !== 'roleId' && filters.roleId !== 'all' && user.roleId !== filters.roleId) return false;
  if (except !== 'departmentId' && filters.departmentId !== 'all' && !userMatchesDepartment(user, filters.departmentId, departments)) {
    return false;
  }
  return true;
}

function uniqueById(rows: NamedOption[]): NamedOption[] {
  const seen = new Map<string, string>();
  for (const row of rows) {
    if (!row.id || seen.has(row.id)) continue;
    seen.set(row.id, row.name || row.id);
  }
  return Array.from(seen, ([id, name]) => ({ id, name }));
}

function ChartTooltip({
  active,
  payload,
  label,
  isLight,
  unit,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color?: string }[];
  label?: string;
  isLight: boolean;
  unit: string;
}) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((item) => Number(item.value) !== 0 || payload.length === 1);
  return (
    <div
      className={`rounded-xl px-3 py-2 text-xs shadow-lg ${
        isLight ? 'bg-white ring-1 ring-black/10 text-neutral-700' : 'bg-neutral-800 ring-1 ring-white/10 text-neutral-200'
      }`}
    >
      {label ? <p className={`font-medium mb-1 ${isLight ? 'text-neutral-900' : 'text-white'}`}>{label}</p> : null}
      {rows.map((item) => (
        <p key={item.name} className="flex items-center gap-2">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: typeof item.color === 'string' ? item.color : 'currentColor' }}
          />
          {item.name}:{' '}
          <span className="tabular-nums font-medium">
            {formatTokens(Number(item.value) || 0)} {unit.toLowerCase()}
          </span>
        </p>
      ))}
    </div>
  );
}

function DailyTooltip({
  active,
  payload,
  isLight,
  unit,
  stacked,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color?: string; dataKey?: string; payload?: DailyPoint }[];
  isLight: boolean;
  unit: string;
  stacked: boolean;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const series = stacked
    ? payload.filter((item) => item.dataKey !== 'average' && Number(item.value) > 0)
    : [];
  const parts: { name: string; tokens: number; color?: string }[] = stacked
    ? series.map((item) => ({
        name: item.name,
        tokens: Number(item.value) || 0,
        color: typeof item.color === 'string' ? item.color : undefined,
      }))
    : row.breakdown;
  return (
    <div
      className={`rounded-xl px-3 py-2 text-xs shadow-lg min-w-[160px] ${
        isLight ? 'bg-white ring-1 ring-black/10 text-neutral-700' : 'bg-neutral-800 ring-1 ring-white/10 text-neutral-200'
      }`}
    >
      <p className={`font-medium mb-1 ${isLight ? 'text-neutral-900' : 'text-white'}`}>{row.label}</p>
      <p className="tabular-nums">
        {formatTokens(row.tokens)} {unit.toLowerCase()}
      </p>
      {row.requestCount > 0 && row.requestCount !== row.tokens ? (
        <p className={isLight ? 'text-neutral-500' : 'text-neutral-400'}>{formatTokens(row.requestCount)} requests</p>
      ) : null}
      <p className={isLight ? 'text-neutral-500' : 'text-neutral-400'}>
        7-day avg {row.average < 10 ? row.average.toFixed(1) : compactTokens(Math.round(row.average))}
      </p>
      {parts.length ? (
        <ul className="mt-1.5 space-y-0.5">
          {parts.slice(0, 6).map((item) => (
            <li key={item.name} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5 min-w-0">
                {item.color ? (
                  <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: item.color }} />
                ) : null}
                <span className="truncate">{item.name}</span>
              </span>
              <span className="tabular-nums shrink-0">{formatTokens(item.tokens)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ChartCard({
  title,
  hint,
  isLight,
  actions,
  children,
}: {
  title: string;
  hint: string;
  isLight: boolean;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  const heading = isLight ? 'text-neutral-900' : 'text-white';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  return (
    <div className={cardClass(isLight)}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className={`text-sm font-medium ${heading}`}>{title}</p>
          <p className={`text-xs mt-0.5 ${muted}`}>{hint}</p>
        </div>
        {actions ? <div className="shrink-0">{actions}</div> : null}
      </div>
      {children}
    </div>
  );
}

function CompactFilter({
  label,
  value,
  options,
  onChange,
  isLight,
}: {
  label: string;
  value: string;
  options: { id: string; label: string }[];
  onChange: (next: string) => void;
  isLight: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.id === value);
  const filtered = options.filter((option) => {
    const haystack = `${option.label} ${option.id}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative w-[148px]">
      <button
        type="button"
        onClick={() => setOpen((next) => !next)}
        className={`${compactControlClass(isLight)} w-full flex items-center justify-between gap-1.5 text-left`}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={selected?.label || label}
      >
        <span className="truncate">{selected?.label || label}</span>
        <ChevronDown size={12} className={`shrink-0 ${open ? 'rotate-180' : ''} ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
      </button>
      {open ? (
        <div
          className={`absolute z-40 mt-1 w-[220px] rounded-xl border shadow-xl overflow-hidden ${
            isLight ? 'bg-white border-neutral-200 shadow-neutral-900/10' : 'bg-neutral-900 border-neutral-700 shadow-black/40'
          }`}
        >
          <div className={`flex items-center gap-2 px-2.5 py-1.5 border-b ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
            <Search size={12} className={isLight ? 'text-neutral-400' : 'text-neutral-500'} />
            <input
              className={`w-full bg-transparent text-xs outline-none ${isLight ? 'text-neutral-900 placeholder:text-neutral-400' : 'text-white placeholder:text-neutral-500'}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search ${label.toLowerCase()}`}
              autoFocus
            />
          </div>
          <ul className="max-h-48 overflow-y-auto py-1" role="listbox">
            {filtered.length ? (
              filtered.map((option) => {
                const checked = option.id === value;
                return (
                  <li key={option.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={checked}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left ${
                        isLight ? 'hover:bg-neutral-50 text-neutral-800' : 'hover:bg-neutral-800 text-neutral-200'
                      } ${checked ? (isLight ? 'bg-indigo-50/70' : 'bg-indigo-500/10') : ''}`}
                      onClick={() => {
                        onChange(option.id);
                        setOpen(false);
                      }}
                    >
                      <span
                        className={`h-3.5 w-3.5 rounded-full border flex items-center justify-center shrink-0 ${
                          checked
                            ? 'border-indigo-500 bg-indigo-600 text-white'
                            : isLight
                              ? 'border-neutral-300 bg-white'
                              : 'border-neutral-600 bg-neutral-900'
                        }`}
                      >
                        {checked ? <Check size={9} strokeWidth={3} /> : null}
                      </span>
                      <span className="truncate">{option.label}</span>
                    </button>
                  </li>
                );
              })
            ) : (
              <li className={`px-2.5 py-2 text-xs ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>No matches</li>
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function FilterChip({
  label,
  onClear,
  isLight,
}: {
  label: string;
  onClear: () => void;
  isLight: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClear}
      className={`inline-flex h-6 items-center gap-1 rounded-full px-2 text-[11px] ${
        isLight ? 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200' : 'bg-neutral-800 text-neutral-200 hover:bg-neutral-700'
      }`}
    >
      <span className="max-w-[160px] truncate">{label}</span>
      <X size={11} />
    </button>
  );
}

function RankBarChart({
  data,
  isLight,
  unit,
  metric,
  color,
  axis,
  grid,
  labelFor,
}: {
  data: { name: string; tokens: number; role?: string }[];
  isLight: boolean;
  unit: string;
  metric: StatisticsMetric;
  color: string;
  axis: string;
  grid: string;
  labelFor?: (row: { name: string; role?: string }) => string;
}) {
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  if (!data.length) {
    return <p className={`text-xs py-8 text-center ${muted}`}>Nothing to rank in this filter.</p>;
  }
  const height = Math.max(176, Math.min(288, data.length * 34 + 28));
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 48, left: 4, bottom: 0 }}>
          <CartesianGrid stroke={grid} strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fill: axis, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value: number) => compactMetric(value, metric)}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fill: axis, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={112}
            tickFormatter={(value: string) => axisName(value)}
          />
          <Tooltip
            content={<ChartTooltip isLight={isLight} unit={unit} />}
            labelFormatter={(_, payload) => {
              const row = payload?.[0]?.payload as { name?: string; role?: string } | undefined;
              if (!row?.name) return '';
              return labelFor ? labelFor({ name: row.name, role: row.role }) : row.name;
            }}
          />
          <Bar dataKey="tokens" name={unit} radius={[0, 6, 6, 0]} barSize={14} fill={color}>
            <LabelList
              dataKey="tokens"
              position="right"
              formatter={(value: number) => compactMetric(Number(value) || 0, metric)}
              fill={axis}
              fontSize={10}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function groupSpend(rows: { name: string; tokens: number }[]): { name: string; tokens: number }[] {
  const byName = new Map<string, number>();
  for (const row of rows) byName.set(row.name, (byName.get(row.name) ?? 0) + row.tokens);
  return Array.from(byName.entries())
    .map(([name, tokens]) => ({ name, tokens }))
    .sort((left, right) => right.tokens - left.tokens)
    .slice(0, RANK_LIMIT);
}

function buildDailySeries(
  users: StatisticsMapped[],
  month: string,
  resources: NamedOption[],
  stacked: boolean
): { points: DailyPoint[]; stackKeys: string[] } {
  const dates = daysInMonth(month);
  const byDate = new Map<string, { tokens: number; requestCount: number; byResource: Map<string, number> }>();
  for (const date of dates) {
    byDate.set(date, { tokens: 0, requestCount: 0, byResource: new Map() });
  }
  const resourceName = (id: string) => resources.find((item) => item.id === id)?.name || id;
  for (const user of users) {
    const name = resourceName(user.resourceId);
    for (const point of user.dailyUsage) {
      if (!point.date.startsWith(`${month}-`)) continue;
      const bucket = byDate.get(point.date);
      if (!bucket) continue;
      bucket.tokens += point.tokens;
      bucket.requestCount += point.requestCount || 0;
      bucket.byResource.set(name, (bucket.byResource.get(name) ?? 0) + point.tokens);
    }
  }
  const totals = new Map<string, number>();
  Array.from(byDate.values()).forEach((bucket) => {
    Array.from(bucket.byResource.entries()).forEach(([name, tokens]) => {
      totals.set(name, (totals.get(name) ?? 0) + tokens);
    });
  });
  const ranked = Array.from(totals.entries()).sort((left, right) => right[1] - left[1]);
  const top = ranked.slice(0, STACK_LIMIT).map(([name]) => name);
  const topSet = new Set(top);
  const hasOther = ranked.length > top.length;
  const stackKeys = stacked ? [...top.map(asStackKey), ...(hasOther ? [asStackKey('Other')] : [])] : [];
  const points = dates.map((date, index) => {
    const bucket = byDate.get(date) || { tokens: 0, requestCount: 0, byResource: new Map<string, number>() };
    const window = dates.slice(Math.max(0, index - 6), index + 1);
    const average = window.reduce((sum, item) => sum + (byDate.get(item)?.tokens ?? 0), 0) / window.length;
    const breakdown = Array.from(bucket.byResource.entries())
      .filter(([, tokens]) => tokens > 0)
      .sort((left, right) => right[1] - left[1])
      .map(([name, tokens]) => ({ name, tokens }));
    const row: DailyPoint = {
      date,
      label: dayLabel(date),
      tokens: bucket.tokens,
      requestCount: bucket.requestCount,
      average: Math.round(average * 10) / 10,
      breakdown,
    };
    if (stacked) {
      let other = 0;
      Array.from(bucket.byResource.entries()).forEach(([name, tokens]) => {
        if (topSet.has(name)) row[asStackKey(name)] = tokens;
        else other += tokens;
      });
      for (const name of top) {
        const key = asStackKey(name);
        if (typeof row[key] !== 'number') row[key] = 0;
      }
      if (hasOther) row[asStackKey('Other')] = other;
    }
    return row;
  });
  return { points, stackKeys };
}

function toQuotaUser(row: StatisticsUserRow, directory: QuotaDirectory, month: string): StatisticsMapped {
  const person = directory.users.find((item) => item.id === row.userId);
  const resourceId = row.resourceId || row.clientId;
  return {
    id: `${row.userId}::${row.clientId}::${resourceId}`,
    clientId: row.clientId,
    userId: row.userId,
    resourceId,
    resourceType: row.resourceType,
    name: person?.name || row.userId,
    email: person?.email || '',
    role: person?.roleName || '',
    roleId: person?.roleId || '',
    departmentId: person?.departmentId || '',
    tokenLimit: row.tokenLimit,
    tokensUsed: row.tokensUsed,
    promptTokens: Number(row.promptTokens) || 0,
    completionTokens: Number(row.completionTokens) || 0,
    requestCount: Number(row.requestCount) || 0,
    dailyUsage: (row.dailyUsage || []).map((point) => ({
      date: point.date,
      tokens: point.tokens,
      requestCount: Number(point.requestCount) || 0,
    })),
    byModel: (row.byModel || []).map((point) => ({ model: point.model, tokens: point.tokens })),
    updatedAt: row.updatedAt || '',
    month,
  };
}

function emptyCopy(kind: QuotaKind): { title: string; description: string } {
  if (kind === 'tool') {
    return {
      title: 'No tool usage',
      description: 'Nothing matches these filters. Reset or choose another tool, client, department, role, user, or month.',
    };
  }
  if (kind === 'llm') {
    return {
      title: 'No AI gateway usage',
      description: 'Nothing matches these filters. Reset or choose another gateway, client, department, role, user, or month.',
    };
  }
  return {
    title: 'No spending data',
    description: 'Nothing matches these filters. Reset or choose another client, department, role, user, or month.',
  };
}

export default function UsageStatisticsTab({ isLight }: { isLight: boolean }) {
  const { quotaDirectory } = useGovernedMcp();
  const [kind, setKind] = useState<QuotaKind>('tool');
  const [month, setMonth] = useState(currentQuotaMonth());
  const [clientId, setClientId] = useState(EMPTY_FILTERS.clientId);
  const [resourceId, setResourceId] = useState(EMPTY_FILTERS.resourceId);
  const [departmentId, setDepartmentId] = useState(EMPTY_FILTERS.departmentId);
  const [roleId, setRoleId] = useState(EMPTY_FILTERS.roleId);
  const [userId, setUserId] = useState(EMPTY_FILTERS.userId);
  const [dailyMode, setDailyMode] = useState<DailyMode>('total');
  const [live, setLive] = useState(false);
  const [payload, setPayload] = useState<AiGovernanceStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  const inFlightRef = useRef(false);
  const requestIdRef = useRef(0);

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const title = isLight ? 'text-neutral-900' : 'text-white';
  const axis = isLight ? '#94a3b8' : '#737373';
  const grid = isLight ? '#f1f5f9' : '#27272a';
  const modelColors = isLight ? MODEL_COLORS_LIGHT : MODEL_COLORS_DARK;
  const stackColors = isLight ? STACK_COLORS_LIGHT : STACK_COLORS_DARK;
  const resourceType = resourceTypeFromQuotaKind(kind);
  const metric: StatisticsMetric = payload?.metric || (kind === 'tool' ? 'calls' : 'tokens');
  const unit = unitLabel(metric);
  const filters = useMemo(
    () => ({ clientId, resourceId, departmentId, roleId, userId }),
    [clientId, departmentId, resourceId, roleId, userId]
  );
  const load = useCallback(async (nextMonth: string, nextKind: QuotaKind, reason: 'page' | 'refresh' | 'live' = 'page') => {
    const silent = reason !== 'page';
    if (silent && inFlightRef.current) {
      logGovernedMcp('app', 'quota_statistics_refresh_skipped', { reason, month: nextMonth, kind: nextKind });
      return;
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    inFlightRef.current = true;
    if (silent) setRefreshing(true);
    else {
      setLoading(true);
      setError(null);
    }
    const nextType = resourceTypeFromQuotaKind(nextKind);
    try {
      const data = await aiGovernanceDashboardApi.getStatistics(nextMonth, nextType);
      if (requestId !== requestIdRef.current) return;
      setPayload(data);
      setLastFetchedAt(new Date().toISOString());
      if (!silent) setError(null);
      if (reason === 'live') {
        logGovernedMcp('app', 'quota_statistics_live_refresh_ok', {
          month: nextMonth,
          kind: nextKind,
          projectionAsOf: data?.projectionAsOf,
          users: data?.users?.length,
        });
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      const message = err instanceof Error ? err.message : 'Unable to load usage statistics.';
      if (silent) {
        logGovernedMcp('warning', reason === 'refresh' ? 'quota_statistics_refresh_failed' : 'quota_statistics_live_refresh_failed', {
          month: nextMonth,
          kind: nextKind,
          message,
        });
      } else {
        setPayload(null);
        setError(message);
      }
    } finally {
      if (requestId === requestIdRef.current) inFlightRef.current = false;
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(month, kind);
  }, [kind, load, month]);

  useEffect(() => {
    if (!live) return undefined;
    void load(month, kind, 'live');
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      void load(month, kind, 'live');
    }, LIVE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [kind, live, load, month]);

  const universe = useMemo(() => {
    const clients = (payload?.clients || []).filter((item) => item.id);
    const resources = (payload?.resources || [])
      .filter((item) => item.id)
      .map((item) => ({ ...item, name: resourceDisplayName(kind, item.id, item.name) }));
    const mapped = (payload?.users || []).map((row) => toQuotaUser(row, quotaDirectory, month));
    const peopleIds = new Set(mapped.map((user) => user.userId).filter(Boolean) as string[]);
    const people = quotaDirectory.users.filter((item) => peopleIds.has(item.id)).map((item) => ({ id: item.id, name: item.name }));
    for (const user of mapped) {
      if (user.userId && !people.some((item) => item.id === user.userId)) {
        people.push({ id: user.userId, name: user.name });
      }
    }
    return {
      users: mapped,
      clients,
      resources,
      departments: quotaDirectory.departments.map((item) => ({ id: item.id, name: item.name })),
      roles: quotaDirectory.roles.map((item) => ({ id: item.id, name: item.name })),
      people,
    };
  }, [kind, month, payload, quotaDirectory]);

  const liveOptions = useMemo(() => {
    const rowsFor = (except?: FilterDim) =>
      universe.users.filter((user) => userMatches(user, filters, quotaDirectory.departments, except));
    const named = (id: string, name: string): NamedOption => ({ id, name: name || id });
    const clients = uniqueById(
      rowsFor('clientId').map((user) => named(user.clientId, universe.clients.find((item) => item.id === user.clientId)?.name || user.clientId))
    );
    const resources = uniqueById(
      rowsFor('resourceId').map((user) =>
        named(user.resourceId, resourceDisplayName(kind, user.resourceId, universe.resources.find((item) => item.id === user.resourceId)?.name))
      )
    );
    const people = uniqueById(
      rowsFor('userId')
        .filter((user): user is StatisticsMapped & { userId: string } => Boolean(user.userId))
        .map((user) => named(user.userId, universe.people.find((item) => item.id === user.userId)?.name || user.name))
    );
    const roles = uniqueById(
      rowsFor('roleId')
        .filter((user): user is StatisticsMapped & { roleId: string } => Boolean(user.roleId))
        .map((user) => named(user.roleId, user.role || universe.roles.find((item) => item.id === user.roleId)?.name || user.roleId))
    );
    const departments = uniqueById(
      rowsFor('departmentId')
        .filter((user) => user.departmentId)
        .map((user) => {
          const departmentId = user.departmentId || '';
          const department = quotaDirectory.departments.find(
            (item) => item.id === departmentId || departmentMatches(item, departmentId)
          );
          return named(department?.id || departmentId, department?.name || departmentId);
        })
    );
    return { clients, resources, people, roles, departments };
  }, [filters, kind, quotaDirectory.departments, universe]);

  useEffect(() => {
    if (loading) return;
    const missing = (id: string, options: NamedOption[]) => id !== 'all' && !options.some((item) => item.id === id);
    if (missing(clientId, liveOptions.clients)) {
      logGovernedMcp('app', 'quota_statistics_filter_cleared', { dimension: 'client', clientId });
      setClientId('all');
    }
    if (missing(resourceId, liveOptions.resources)) {
      logGovernedMcp('app', 'quota_statistics_filter_cleared', { dimension: 'resource', resourceType, resourceId });
      setResourceId('all');
    }
    if (missing(departmentId, liveOptions.departments)) {
      logGovernedMcp('app', 'quota_statistics_filter_cleared', { dimension: 'department', departmentId });
      setDepartmentId('all');
    }
    if (missing(roleId, liveOptions.roles)) {
      logGovernedMcp('app', 'quota_statistics_filter_cleared', { dimension: 'role', roleId });
      setRoleId('all');
    }
    if (missing(userId, liveOptions.people)) {
      logGovernedMcp('app', 'quota_statistics_filter_cleared', { dimension: 'user', userId });
      setUserId('all');
    }
  }, [clientId, departmentId, liveOptions, loading, resourceId, resourceType, roleId, userId]);

  const users = useMemo(
    () => universe.users.filter((user) => userMatches(user, filters, quotaDirectory.departments)),
    [filters, quotaDirectory.departments, universe.users]
  );

  const filtersDirty =
    clientId !== 'all' ||
    resourceId !== 'all' ||
    departmentId !== 'all' ||
    roleId !== 'all' ||
    userId !== 'all' ||
    month !== currentQuotaMonth();

  const resetFilters = () => {
    setMonth(currentQuotaMonth());
    setClientId(EMPTY_FILTERS.clientId);
    setResourceId(EMPTY_FILTERS.resourceId);
    setDepartmentId(EMPTY_FILTERS.departmentId);
    setRoleId(EMPTY_FILTERS.roleId);
    setUserId(EMPTY_FILTERS.userId);
    logGovernedMcp('app', 'quota_statistics_reset', { kind });
  };

  const totals = useMemo(() => {
    return users.reduce(
      (acc, user) => {
        acc.limit += user.tokenLimit;
        acc.used += user.tokensUsed;
        acc.remaining += remainingTokens(user);
        acc.promptTokens += user.promptTokens;
        acc.completionTokens += user.completionTokens;
        acc.requestCount += user.requestCount;
        if (user.tokensUsed >= user.tokenLimit && user.tokenLimit > 0) acc.over += 1;
        return acc;
      },
      { limit: 0, used: 0, remaining: 0, over: 0, promptTokens: 0, completionTokens: 0, requestCount: 0 }
    );
  }, [users]);

  const utilization = totals.limit > 0 ? Math.min(100, Math.round((totals.used / totals.limit) * 100)) : 0;
  const peopleCount = new Set(users.map((user) => user.userId || user.id)).size;
  const stackedDaily = kind !== 'client' && dailyMode === 'stack';

  const daily = useMemo(
    () => buildDailySeries(users, month, universe.resources, stackedDaily),
    [month, stackedDaily, universe.resources, users]
  );

  const dailySummary = useMemo(() => {
    const active = daily.points.filter((point) => point.tokens > 0);
    const peak = active.reduce<DailyPoint | null>((best, point) => (!best || point.tokens > best.tokens ? point : best), null);
    const average = active.length ? Math.round(active.reduce((sum, point) => sum + point.tokens, 0) / active.length) : 0;
    return { activeDays: active.length, peak, average };
  }, [daily.points]);

  useEffect(() => {
    if (loading || !users.length) return;
    if (dailySummary.activeDays === 0) {
      logGovernedMcp('warning', 'quota_statistics_daily_empty', { month, kind, users: users.length });
      return;
    }
    logGovernedMcp('app', 'quota_statistics_daily_ready', {
      month,
      kind,
      days: daily.points.length,
      activeDays: dailySummary.activeDays,
      peakDay: dailySummary.peak?.date,
      stacked: stackedDaily,
    });
  }, [daily.points.length, dailySummary.activeDays, dailySummary.peak?.date, kind, loading, month, stackedDaily, users.length]);

  const byUser = useMemo(
    () =>
      users
        .reduce<QuotaUser[]>((acc, user) => {
          const existing = acc.find((item) => (item.userId || item.id) === (user.userId || user.id));
          if (!existing) {
            acc.push({ ...user });
            return acc;
          }
          existing.tokensUsed += user.tokensUsed;
          existing.tokenLimit += user.tokenLimit;
          return acc;
        }, [])
        .map((user) => ({
          id: user.userId || user.id,
          name: user.name,
          fullName: user.name,
          role: user.role,
          tokens: user.tokensUsed,
          remaining: remainingTokens(user),
          percent: utilizationPercent(user),
        }))
        .sort((left, right) => right.tokens - left.tokens),
    [users]
  );

  const byDepartment = useMemo(
    () =>
      groupSpend(
        users.map((user) => ({
          name:
            universe.departments.find((item) => item.id === user.departmentId)?.name ||
            quotaDirectory.departments.find((item) => item.id === user.departmentId)?.name ||
            'Unassigned',
          tokens: user.tokensUsed,
        }))
      ),
    [quotaDirectory.departments, universe.departments, users]
  );

  const byRole = useMemo(
    () =>
      groupSpend(
        users.map((user) => ({
          name: user.role || universe.roles.find((item) => item.id === user.roleId)?.name || 'Unassigned',
          tokens: user.tokensUsed,
        }))
      ),
    [universe.roles, users]
  );

  const byClient = useMemo(
    () =>
      groupSpend(
        users.map((user) => ({
          name: clientLabel(user.clientId, universe.clients),
          tokens: user.tokensUsed,
        }))
      ),
    [universe.clients, users]
  );

  const byResource = useMemo(
    () =>
      groupSpend(
        users.map((user) => ({
          name: resourceDisplayName(kind, user.resourceId, universe.resources.find((item) => item.id === user.resourceId)?.name),
          tokens: user.tokensUsed,
        }))
      ),
    [kind, universe.resources, users]
  );

  const byModel = useMemo(() => aggregateModelSpend(users), [users]);
  const mix = useMemo(() => utilizationBreakdown(users), [users]);
  const modelTotal = byModel.reduce((sum, row) => sum + row.tokens, 0);
  const mixTotal = mix.reduce((sum, row) => sum + row.value, 0);
  const usedVsRemain = byUser.slice(0, RANK_LIMIT).map((row) => ({
    name: row.fullName,
    Used: row.tokens,
    Remaining: row.remaining,
  }));

  const empty = emptyCopy(kind);
  const resourceFilterLabel = kind === 'tool' ? 'Tool' : kind === 'llm' ? 'Gateway' : '';
  const resourceColumn = kind === 'tool' ? 'Tool' : kind === 'llm' ? 'Gateway' : '';
  const usedColumn = metric === 'calls' ? 'Calls' : 'Used';
  const showModel = byModel.length > 0;
  const showResourceChart = kind !== 'client';
  const isLlm = kind === 'llm';
  const dailyHint = dailySummary.peak
    ? `${formatQuotaMonth(month)} · peak ${dailySummary.peak.label} · avg ${compactMetric(dailySummary.average, metric)} on ${dailySummary.activeDays} active days`
    : `${formatQuotaMonth(month)} · no daily points recorded`;
  const summaryCards =
    isLlm
      ? [
          {
            label: 'Requests',
            value: formatTokens(totals.requestCount),
            hint: `${peopleCount} people`,
          },
          {
            label: 'Tokens in',
            value: compactTokens(totals.promptTokens),
            hint: `${formatTokens(totals.promptTokens)} prompt`,
          },
          {
            label: 'Tokens out',
            value: compactTokens(totals.completionTokens),
            hint: `${formatTokens(totals.completionTokens)} completion`,
          },
          {
            label: 'Total tokens',
            value: compactTokens(totals.used),
            hint: `${formatTokens(totals.used)} in + out`,
          },
        ]
      : [
          {
            label: metric === 'calls' ? 'Current usage' : 'Current spend',
            value: compactMetric(totals.used, metric),
            hint: `${formatMetric(totals.used, metric)} ${unit.toLowerCase()}`,
          },
          {
            label: 'Remaining',
            value: compactMetric(totals.remaining, metric),
            hint: `of ${compactMetric(totals.limit, metric)} assigned`,
          },
          { label: 'Utilization', value: `${utilization}%`, hint: `${totals.over} over limit` },
          {
            label: 'People',
            value: String(peopleCount),
            hint: `${users.length} ${kind === 'tool' ? 'tool' : 'client'} assignments`,
          },
        ];

  const optionList = (allLabel: string, rows: NamedOption[], selectedId: string, selectedName?: string) => {
    const items = [{ id: 'all', label: allLabel }, ...rows.map((item) => ({ id: item.id, label: item.name || item.id }))];
    if (selectedId !== 'all' && !items.some((item) => item.id === selectedId)) {
      items.push({ id: selectedId, label: selectedName || selectedId });
    }
    return items;
  };

  const updatedStamp = formatUpdatedTime(payload?.projectionAsOf || lastFetchedAt || '');
  const busy = loading || refreshing;
  const liveTrackOff = isLight ? 'bg-neutral-300' : 'bg-neutral-600';

  return (
    <div className="space-y-4">
      <div className={`${cardClass(isLight)} !p-3 space-y-2.5 overflow-visible`}>
        <div className="flex flex-wrap items-center gap-2">
          <PillTabs
            isLight={isLight}
            ariaLabel="Usage resource type"
            value={kind}
            items={KIND_TABS}
            size="sm"
            onChange={(next) => {
              setKind(next);
              setResourceId('all');
              setDailyMode('total');
              logGovernedMcp('app', 'quota_statistics_kind', { kind: next, resourceType: resourceTypeFromQuotaKind(next) });
            }}
          />
          <div className="flex flex-wrap items-center gap-1.5 ml-auto">
            {updatedStamp ? <span className={`text-[11px] tabular-nums ${muted}`}>Updated {updatedStamp}</span> : null}
            <button
              type="button"
              className={`${ghostBtn(isLight)} !h-8 !text-xs`}
              disabled={busy}
              title="Reload now"
              onClick={() => {
                logGovernedMcp('app', 'quota_statistics_refresh', { month, kind, resourceType });
                void load(month, kind, 'refresh');
              }}
            >
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              type="button"
              role="switch"
              aria-checked={live}
              aria-label="Live updates"
              title={`Poll every ${LIVE_REFRESH_MS / 1000} seconds while enabled`}
              className={`${ghostBtn(isLight)} !h-8 !text-xs`}
              onClick={() => {
                const next = !live;
                setLive(next);
                logGovernedMcp('app', 'quota_statistics_live', { live: next, month, kind });
              }}
            >
              <span
                className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
                  live ? 'bg-indigo-600' : liveTrackOff
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${
                    live ? 'translate-x-3' : 'translate-x-0'
                  }`}
                />
              </span>
              Live
            </button>
            <button type="button" className={`${ghostBtn(isLight)} !h-8 !text-xs`} onClick={resetFilters} disabled={!filtersDirty}>
              <RotateCcw size={12} />
              Reset
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            type="month"
            aria-label="Month"
            className={`${compactControlClass(isLight)} w-[148px]`}
            value={month}
            onChange={(event) => {
              const next = event.target.value;
              logGovernedMcp('app', 'quota_statistics_month', { month: next });
              setMonth(next);
            }}
          />
          <CompactFilter
            label="Client"
            isLight={isLight}
            value={clientId}
            options={optionList(
              'All clients',
              liveOptions.clients,
              clientId,
              universe.clients.find((item) => item.id === clientId)?.name
            )}
            onChange={(next) => {
              logGovernedMcp('app', 'quota_statistics_client_filter', { clientId: next });
              setClientId(next);
            }}
          />
          {kind !== 'client' ? (
            <CompactFilter
              label={resourceFilterLabel}
              isLight={isLight}
              value={resourceId}
              options={optionList(
                kind === 'tool' ? 'All tools' : 'All gateways',
                liveOptions.resources,
                resourceId,
                universe.resources.find((item) => item.id === resourceId)?.name
              )}
              onChange={(next) => {
                logGovernedMcp('app', 'quota_statistics_resource_filter', { resourceType, resourceId: next });
                setResourceId(next);
              }}
            />
          ) : null}
          <CompactFilter
            label="Department"
            isLight={isLight}
            value={departmentId}
            options={optionList(
              'All departments',
              liveOptions.departments,
              departmentId,
              liveOptions.departments.find((item) => item.id === departmentId)?.name ||
                universe.departments.find((item) => item.id === departmentId)?.name
            )}
            onChange={(next) => {
              logGovernedMcp('app', 'quota_statistics_department_filter', { departmentId: next });
              setDepartmentId(next);
            }}
          />
          <CompactFilter
            label="Role"
            isLight={isLight}
            value={roleId}
            options={optionList(
              'All roles',
              liveOptions.roles,
              roleId,
              liveOptions.roles.find((item) => item.id === roleId)?.name || universe.roles.find((item) => item.id === roleId)?.name
            )}
            onChange={(next) => {
              logGovernedMcp('app', 'quota_statistics_role_filter', { roleId: next });
              setRoleId(next);
            }}
          />
          <CompactFilter
            label="User"
            isLight={isLight}
            value={userId}
            options={optionList(
              'All users',
              liveOptions.people,
              userId,
              liveOptions.people.find((item) => item.id === userId)?.name || universe.people.find((item) => item.id === userId)?.name
            )}
            onChange={(next) => {
              logGovernedMcp('app', 'quota_statistics_user_filter', { userId: next });
              setUserId(next);
            }}
          />
        </div>
        {filtersDirty ? (
          <div className="flex flex-wrap gap-1.5">
            {month !== currentQuotaMonth() ? (
              <FilterChip
                label={formatQuotaMonth(month)}
                isLight={isLight}
                onClear={() => {
                  const next = currentQuotaMonth();
                  logGovernedMcp('app', 'quota_statistics_month', { month: next });
                  setMonth(next);
                }}
              />
            ) : null}
            {clientId !== 'all' ? (
              <FilterChip
                label={`Client · ${liveOptions.clients.find((item) => item.id === clientId)?.name || clientId}`}
                isLight={isLight}
                onClear={() => {
                  logGovernedMcp('app', 'quota_statistics_client_filter', { clientId: 'all' });
                  setClientId('all');
                }}
              />
            ) : null}
            {resourceId !== 'all' && kind !== 'client' ? (
              <FilterChip
                label={`${resourceFilterLabel} · ${liveOptions.resources.find((item) => item.id === resourceId)?.name || resourceId}`}
                isLight={isLight}
                onClear={() => {
                  logGovernedMcp('app', 'quota_statistics_resource_filter', { resourceType, resourceId: 'all' });
                  setResourceId('all');
                }}
              />
            ) : null}
            {departmentId !== 'all' ? (
              <FilterChip
                label={`Dept · ${liveOptions.departments.find((item) => item.id === departmentId)?.name || departmentId}`}
                isLight={isLight}
                onClear={() => {
                  logGovernedMcp('app', 'quota_statistics_department_filter', { departmentId: 'all' });
                  setDepartmentId('all');
                }}
              />
            ) : null}
            {roleId !== 'all' ? (
              <FilterChip
                label={`Role · ${liveOptions.roles.find((item) => item.id === roleId)?.name || roleId}`}
                isLight={isLight}
                onClear={() => {
                  logGovernedMcp('app', 'quota_statistics_role_filter', { roleId: 'all' });
                  setRoleId('all');
                }}
              />
            ) : null}
            {userId !== 'all' ? (
              <FilterChip
                label={`User · ${liveOptions.people.find((item) => item.id === userId)?.name || userId}`}
                isLight={isLight}
                onClear={() => {
                  logGovernedMcp('app', 'quota_statistics_user_filter', { userId: 'all' });
                  setUserId('all');
                }}
              />
            ) : null}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className={`${cardClass(isLight)} py-10 text-center`}>
          <p className={`text-sm font-medium ${title}`}>Loading usage statistics</p>
          <p className={`text-xs mt-1 ${muted}`}>
            {kind === 'tool'
              ? 'Tool calls from the reporting projection.'
              : kind === 'llm'
                ? 'AI gateway spend from the reporting projection.'
                : 'Client spend from the reporting projection. Daily and model series only appear when recorded.'}
          </p>
        </div>
      ) : null}

      {!loading && error ? <EmptyState isLight={isLight} title="Usage statistics unavailable" description={error} /> : null}

      {!loading && !error && users.length === 0 ? <EmptyState isLight={isLight} title={empty.title} description={empty.description} /> : null}

      {!loading && !error && users.length > 0 ? (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            {summaryCards.map((item) => (
              <div key={item.label} className={cardClass(isLight)}>
                <p className={`text-[11px] uppercase tracking-wide ${muted}`}>{item.label}</p>
                <p className={`mt-1 text-xl font-semibold tabular-nums tracking-tight ${title}`}>{item.value}</p>
                <p className={`mt-0.5 text-xs ${muted}`}>{item.hint}</p>
              </div>
            ))}
          </div>

          <ChartCard
            title={metric === 'calls' ? 'Daily usage' : 'Daily spend'}
            hint={dailyHint}
            isLight={isLight}
            actions={
              kind !== 'client' ? (
                <PillTabs
                  isLight={isLight}
                  ariaLabel="Daily series"
                  value={dailyMode}
                  items={[
                    { id: 'total', label: 'Total' },
                    { id: 'stack', label: kind === 'tool' ? 'By tool' : 'By gateway' },
                  ]}
                  size="sm"
                  onChange={(next) => {
                    setDailyMode(next);
                    logGovernedMcp('app', 'quota_statistics_daily_mode', { kind, mode: next });
                  }}
                />
              ) : undefined
            }
          >
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={daily.points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={18} />
                  <YAxis
                    tick={{ fill: axis, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={46}
                    tickFormatter={(value: number) => compactMetric(value, metric)}
                  />
                  <Tooltip content={<DailyTooltip isLight={isLight} unit={unit} stacked={stackedDaily} />} />
                  {stackedDaily ? (
                    daily.stackKeys.map((key, index) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        name={fromStackKey(key)}
                        stackId="daily"
                        fill={stackColors[index % stackColors.length]}
                        radius={index === daily.stackKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                        maxBarSize={22}
                      />
                    ))
                  ) : (
                    <Bar
                      dataKey="tokens"
                      name={unit}
                      fill={isLight ? PALETTE.daily.light : PALETTE.daily.dark}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={22}
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="average"
                    name="7-day avg"
                    stroke={isLight ? PALETTE.average.light : PALETTE.average.dark}
                    strokeWidth={2}
                    dot={false}
                    legendType="none"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <ul className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
              {stackedDaily
                ? daily.stackKeys.map((key, index) => (
                    <li key={key} className={`flex items-center gap-1.5 text-[11px] ${muted}`}>
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: stackColors[index % stackColors.length] }} />
                      {fromStackKey(key)}
                    </li>
                  ))
                : (
                    <li className={`flex items-center gap-1.5 text-[11px] ${muted}`}>
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: isLight ? PALETTE.daily.light : PALETTE.daily.dark }}
                      />
                      {unit}
                    </li>
                  )}
              <li className={`flex items-center gap-1.5 text-[11px] ${muted}`}>
                <span className="h-1.5 w-4 rounded-sm" style={{ background: isLight ? PALETTE.average.light : PALETTE.average.dark }} />
                7-day avg
              </li>
            </ul>
          </ChartCard>

          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard title="Top people" hint={`Highest ${unit.toLowerCase()} in this filter`} isLight={isLight}>
              <RankBarChart
                data={byUser.slice(0, RANK_LIMIT)}
                isLight={isLight}
                unit={unit}
                metric={metric}
                color={isLight ? PALETTE.people.light : PALETTE.people.dark}
                axis={axis}
                grid={grid}
                labelFor={(row) => (row.role ? `${row.name} · ${row.role}` : row.name)}
              />
            </ChartCard>

            <ChartCard title="Used vs remaining" hint="Capacity left for the top people" isLight={isLight}>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={usedVsRemain} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 0 }}>
                    <CartesianGrid stroke={grid} strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fill: axis, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value: number) => compactMetric(value, metric)}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fill: axis, fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={112}
                      tickFormatter={(value: string) => axisName(value)}
                    />
                    <Tooltip content={<ChartTooltip isLight={isLight} unit={unit} />} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: axis }} />
                    <Bar dataKey="Used" stackId="cap" fill={isLight ? PALETTE.people.light : PALETTE.people.dark} barSize={14} />
                    <Bar
                      dataKey="Remaining"
                      stackId="cap"
                      fill={isLight ? PALETTE.remain.light : PALETTE.remain.dark}
                      radius={[0, 6, 6, 0]}
                      barSize={14}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard title="Spend by department" hint={`Where ${unit.toLowerCase()} are concentrated`} isLight={isLight}>
              <RankBarChart
                data={byDepartment}
                isLight={isLight}
                unit={unit}
                metric={metric}
                color={isLight ? PALETTE.department.light : PALETTE.department.dark}
                axis={axis}
                grid={grid}
              />
            </ChartCard>
            <ChartCard title="Spend by role" hint="Usage grouped by assigned role" isLight={isLight}>
              <RankBarChart
                data={byRole}
                isLight={isLight}
                unit={unit}
                metric={metric}
                color={isLight ? PALETTE.role.light : PALETTE.role.dark}
                axis={axis}
                grid={grid}
              />
            </ChartCard>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {showResourceChart ? (
              <ChartCard
                title={kind === 'tool' ? 'Spend by tool' : 'Spend by gateway'}
                hint={`${unit} grouped by ${kind === 'tool' ? 'published tool' : 'AI gateway'}`}
                isLight={isLight}
              >
                <RankBarChart
                  data={byResource}
                  isLight={isLight}
                  unit={unit}
                  metric={metric}
                  color={isLight ? PALETTE.resource.light : PALETTE.resource.dark}
                  axis={axis}
                  grid={grid}
                />
              </ChartCard>
            ) : null}
            <ChartCard title="Spend by client" hint={`${unit} per AI client`} isLight={isLight}>
              <RankBarChart
                data={byClient}
                isLight={isLight}
                unit={unit}
                metric={metric}
                color={isLight ? PALETTE.client.light : PALETTE.client.dark}
                axis={axis}
                grid={grid}
              />
            </ChartCard>
          </div>

          {showModel ? (
            <ChartCard title="Spend by model" hint="Share of current token spend" isLight={isLight}>
              <div className="flex items-center gap-4">
                <div className="h-52 w-52 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={byModel} dataKey="tokens" nameKey="model" innerRadius={52} outerRadius={78} paddingAngle={2} stroke="none">
                        {byModel.map((row, index) => (
                          <Cell key={row.model} fill={modelColors[index % modelColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip isLight={isLight} unit={unit} />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="flex-1 min-w-0 space-y-2">
                  {byModel.map((row, index) => {
                    const share = modelTotal > 0 ? Math.round((row.tokens / modelTotal) * 100) : 0;
                    return (
                      <li key={row.model} className="flex items-center justify-between gap-3 text-sm">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="h-2 w-2 rounded-full shrink-0" style={{ background: modelColors[index % modelColors.length] }} />
                          <span className={`truncate ${isLight ? 'text-neutral-700' : 'text-neutral-200'}`}>{row.model}</span>
                        </span>
                        <span className={`tabular-nums shrink-0 ${muted}`}>
                          {compactMetric(row.tokens, metric)} · {share}%
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </ChartCard>
          ) : null}

          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard title="Utilization mix" hint="People by quota health" isLight={isLight}>
              <div className="flex items-center gap-4">
                <div className="relative h-52 w-52 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={mix.filter((row) => row.value > 0)}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={52}
                        outerRadius={78}
                        paddingAngle={2}
                        stroke="none"
                      >
                        {mix
                          .filter((row) => row.value > 0)
                          .map((row) => (
                            <Cell key={row.key} fill={isLight ? STATUS_COLORS[row.key].light : STATUS_COLORS[row.key].dark} />
                          ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip isLight={isLight} unit="Users" />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <p className={`text-lg font-semibold tabular-nums ${title}`}>{peopleCount}</p>
                    <p className={`text-[11px] ${muted}`}>{peopleCount === 1 ? 'person' : 'people'}</p>
                  </div>
                </div>
                <ul className="flex-1 min-w-0 space-y-2">
                  {mix.map((row) => {
                    const share = mixTotal > 0 ? Math.round((row.value / mixTotal) * 100) : 0;
                    return (
                      <li key={row.key} className="flex items-center justify-between gap-3 text-sm">
                        <span className="flex items-center gap-2 min-w-0">
                          <span
                            className="h-2 w-2 rounded-full shrink-0"
                            style={{ background: isLight ? STATUS_COLORS[row.key].light : STATUS_COLORS[row.key].dark }}
                          />
                          <span className={isLight ? 'text-neutral-700' : 'text-neutral-200'}>{row.name}</span>
                        </span>
                        <span className={`tabular-nums shrink-0 ${muted}`}>
                          {row.value} · {share}%
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </ChartCard>

            <ChartCard title="Highest spend" hint="Used versus assigned capacity" isLight={isLight}>
              <ul className="space-y-2.5">
                {byUser.slice(0, 5).map((row) => (
                  <li key={row.id}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${title}`}>{row.fullName}</p>
                        <p className={`text-[11px] truncate ${muted}`}>{row.role || '—'}</p>
                      </div>
                      <p className={`text-xs tabular-nums shrink-0 ${muted}`}>
                        {compactMetric(row.tokens, metric)} · {row.percent}%
                      </p>
                    </div>
                    <div className={`h-1.5 rounded-full overflow-hidden ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
                      <div
                        className={`h-full rounded-full ${
                          row.percent >= 100 ? 'bg-rose-500' : row.percent >= 80 ? 'bg-amber-500' : isLight ? 'bg-indigo-500' : 'bg-indigo-400'
                        }`}
                        style={{ width: `${Math.min(100, row.percent)}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </ChartCard>
          </div>

          <div
            className={`overflow-auto rounded-2xl border shadow-sm ${
              isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-900'
            }`}
          >
            <table className={`w-full text-sm ${isLlm ? 'min-w-[960px]' : 'min-w-[720px]'}`}>
              <thead className={tableHeadClass(isLight)}>
                <tr>
                  {resourceColumn ? <th className={thClass()}>{resourceColumn}</th> : null}
                  <th className={thClass()}>User</th>
                  <th className={thClass()}>Client</th>
                  {isLlm ? (
                    <>
                      <th className={thClass()}>Requests</th>
                      <th className={thClass()}>In</th>
                      <th className={thClass()}>Out</th>
                      <th className={thClass()}>Total</th>
                    </>
                  ) : (
                    <th className={thClass()}>{usedColumn}</th>
                  )}
                  <th className={thClass()}>Limit</th>
                  <th className={thClass()}>Remaining</th>
                  <th className={thClass()}>Status</th>
                </tr>
              </thead>
              <tbody>
                {users.map((row) => {
                  const status = quotaStatus(row);
                  const resourceName = resourceDisplayName(
                    kind,
                    row.resourceId,
                    universe.resources.find((item) => item.id === row.resourceId)?.name
                  );
                  return (
                    <tr key={row.id} className={trClass(isLight)}>
                      {resourceColumn ? (
                        <td className={tdClass()}>
                          <p className={`font-medium ${title}`}>{resourceName}</p>
                        </td>
                      ) : null}
                      <td className={tdClass()}>
                        <p className={`font-medium ${title}`}>{row.name}</p>
                        <p className={`text-[11px] ${muted}`}>{row.role || '—'}</p>
                      </td>
                      <td className={tdClass()}>{clientLabel(row.clientId, universe.clients)}</td>
                      {isLlm ? (
                        <>
                          <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.requestCount)}</td>
                          <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.promptTokens)}</td>
                          <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.completionTokens)}</td>
                          <td className={`${tdClass()} tabular-nums`}>{formatTokens(row.tokensUsed)}</td>
                        </>
                      ) : (
                        <td className={`${tdClass()} tabular-nums`}>{formatMetric(row.tokensUsed, metric)}</td>
                      )}
                      <td className={`${tdClass()} tabular-nums`}>{formatMetric(row.tokenLimit, metric)}</td>
                      <td className={`${tdClass()} tabular-nums`}>{formatMetric(remainingTokens(row), metric)}</td>
                      <td className={tdClass()}>
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${pillTone(status, isLight)}`}>
                          {statusLabel(status)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
